import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, Settings as SettingsIcon, BookOpen } from "lucide-react";
import { BrandWordmark } from "@/components/Brand";
import { unlockApocalypseAudio } from "@/lib/mr/apocalypseAudio";

/**
 * Home — minimal launcher with two pre-flight permission shortcuts.
 *
 *   [ brand mark + wordmark ]
 *
 *   [ Motion (Flip Force) ]   [ Apocalypse FX ]
 *
 *   [ START ]
 *   [ Tutorial ] [ Settings ]
 *
 * iOS Safari demands a user gesture to surface the DeviceMotion /
 * DeviceOrientation / Notification prompts. The magician arms both
 * permissions once on their performance phone via these two pills, then
 * taps Start to enter the disguise. The pills swap label after grant
 * so a quick glance confirms readiness.
 */
export default function Setup() {
  const navigate = useNavigate();
  // idle | prompting | granted | denied | unsupported
  const [motionStatus, setMotionStatus] = useState("idle");
  const [apocStatus, setApocStatus] = useState("idle");

  // -- Flip Force / pass-motion peek ----------------------------------------
  // Requires BOTH DeviceMotion AND DeviceOrientation; iOS Safari treats
  // them as independent grants.
  const grantMotion = async () => {
    const M = typeof window !== "undefined" ? window.DeviceMotionEvent : null;
    const O = typeof window !== "undefined" ? window.DeviceOrientationEvent : null;
    const needsMotion = M && typeof M.requestPermission === "function";
    const needsOrient = O && typeof O.requestPermission === "function";
    if (!M && !O) { setMotionStatus("unsupported"); return; }
    if (!needsMotion && !needsOrient) { setMotionStatus("granted"); return; }
    try {
      setMotionStatus("prompting");
      const results = await Promise.all([
        needsMotion ? M.requestPermission() : Promise.resolve("granted"),
        needsOrient ? O.requestPermission() : Promise.resolve("granted"),
      ]);
      setMotionStatus(results.every((r) => r === "granted") ? "granted" : "denied");
    } catch {
      setMotionStatus("denied");
    }
  };

  // -- Notes Apocalypse -----------------------------------------------------
  // Two distinct pieces both need a user gesture:
  //   1. Web Audio AudioContext unlock — for the synthesised tri-tone
  //      ping that fires on each spawning extra during the glitch.
  //   2. Notification.requestPermission — for the optional banner FX
  //      driven by apocFxPermType="notifications".
  const grantApocalypse = async () => {
    let anyAvailable = false;
    let allGood = true;
    setApocStatus("prompting");

    // (1) AudioContext unlock — synchronous best-effort; only fails on
    //     very old WebKit where the call throws silently.
    try {
      unlockApocalypseAudio();
      anyAvailable = true;
    } catch {
      allGood = false;
    }

    // (2) Notification permission — same dual promise/callback shape
    //     Safari uses, normalised through Promise.resolve.
    try {
      if (typeof Notification !== "undefined") {
        anyAvailable = true;
        if (Notification.permission === "granted") {
          // already good
        } else {
          const res = await Promise.resolve(Notification.requestPermission());
          if (res !== "granted") allGood = false;
        }
      }
    } catch {
      allGood = false;
    }

    if (!anyAvailable) { setApocStatus("unsupported"); return; }
    setApocStatus(allGood ? "granted" : "denied");
  };

  const handleStart = () => navigate("/notes");

  const pillLabel = (status, idleLabel) => {
    if (status === "granted") return "✓ Ready";
    if (status === "denied") return "✗ Retry";
    if (status === "unsupported") return "Unavailable";
    if (status === "prompting") return "Waiting…";
    return idleLabel;
  };

  const pillColor = (status) => {
    if (status === "granted") return "text-emerald-300/90 border-emerald-300/30";
    if (status === "denied") return "text-rose-300/90 border-rose-300/30";
    if (status === "unsupported") return "text-white/40 border-white/10";
    return "text-white/75 border-white/15";
  };

  return (
    <div
      className="min-h-screen w-full px-5 py-10 mr-luxe mr-grain flex items-center justify-center"
      style={{
        background:
          "radial-gradient(900px 500px at 85% 5%, rgba(220,180,60,0.10), transparent 60%), radial-gradient(1100px 700px at 15% 105%, rgba(180,28,40,0.48), transparent 55%), radial-gradient(800px 500px at 50% 50%, rgba(140,20,30,0.18), transparent 70%), #0a0306",
      }}
      data-testid="setup-page"
    >
      <div className="mr-dust" aria-hidden="true" />

      <div className="w-full max-w-md flex flex-col items-center text-center">
        {/* Hero */}
        <div className="mr-rise">
          <BrandWordmark size="xl" aura />
        </div>

        {/* Pre-flight permission pills */}
        <div className="mt-12 w-full mr-rise delay-1">
          <p
            className="text-[10px] text-white/40 mb-2"
            style={{ letterSpacing: "0.32em", textTransform: "uppercase" }}
          >
            Pre-flight
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={grantMotion}
              className={`h-11 rounded-xl bg-black/25 hover:bg-black/35 border ${pillColor(motionStatus)} transition-colors`}
              style={{ letterSpacing: "0.22em", textTransform: "uppercase", fontSize: "10px" }}
              data-testid="grant-motion-btn"
            >
              <span className="block leading-tight">
                <span className="block text-[9px] opacity-60">Flip Force</span>
                <span className="block">{pillLabel(motionStatus, "Grant Motion")}</span>
              </span>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={grantApocalypse}
              className={`h-11 rounded-xl bg-black/25 hover:bg-black/35 border ${pillColor(apocStatus)} transition-colors`}
              style={{ letterSpacing: "0.22em", textTransform: "uppercase", fontSize: "10px" }}
              data-testid="grant-apocalypse-btn"
            >
              <span className="block leading-tight">
                <span className="block text-[9px] opacity-60">Apocalypse</span>
                <span className="block">{pillLabel(apocStatus, "Grant FX")}</span>
              </span>
            </Button>
          </div>
        </div>

        {/* Primary CTA + secondary nav row */}
        <div className="mt-6 w-full mr-rise delay-2">
          <Button
            onClick={handleStart}
            className="w-full h-14 text-base font-semibold rounded-2xl text-[#f0ead4] transition-all mr-cta mr-cta-breath"
            style={{
              background: "linear-gradient(180deg, #a11421 0%, #6c0c16 100%)",
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              fontSize: "13px",
            }}
            data-testid="start-trick-btn"
          >
            Start
            <ChevronRight className="w-5 h-5 ml-2 mr-chev" />
          </Button>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/tutorial")}
              className="h-12 text-white/70 hover:text-white hover:bg-white/5 rounded-2xl"
              style={{ letterSpacing: "0.28em", textTransform: "uppercase", fontSize: "11px" }}
              data-testid="open-tutorial-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" /> Tutorial
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/settings")}
              className="h-12 text-white/70 hover:text-white hover:bg-white/5 rounded-2xl"
              style={{ letterSpacing: "0.28em", textTransform: "uppercase", fontSize: "11px" }}
              data-testid="open-settings-btn"
            >
              <SettingsIcon className="w-4 h-4 mr-2" /> Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
